import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useApiClientWrapper } from 'hooks';
import { RootState } from 'store';
import { fetchAutomationMergeTagsThunk } from '@spike/automations-action';
import { type MergeTag } from '@spike/automation-model';

export interface MergeTagsState {
    tags: MergeTag[];
    required: MergeTag[];
    loading: boolean;
}

export const useMergeTags = (trigger: { event?: string; anchor?: string }): MergeTagsState => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const key = trigger.event ?? trigger.anchor ?? '';
    const catalog = useSelector((state: RootState) => state.automations.mergeTags?.[key]);

    // Tracked per trigger key: switching triggers should fetch the new one, but
    // never refire for one already requested. The api wrapper is a fresh object
    // every render and must stay out of the dependency array.
    const requested = useRef<Set<string>>(new Set());

    useEffect(() => {
        if (catalog || requested.current.has(key)) return;
        requested.current.add(key);

        (dispatch as any)(fetchAutomationMergeTagsThunk(apiClientWrapper, trigger)).catch(() => {
            requested.current.delete(key);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, catalog]);

    return useMemo(() => {
        const tags = catalog?.tags ?? [];

        return {
            tags,
            required: tags.filter(tag => tag.required),
            loading: !catalog
        };
    }, [catalog, key]);
};

export default useMergeTags;
